'use client';

import React, { createContext, useContext, useState, useCallback } from 'react';

/**
 * 텍스트 선택 영역의 현재 스타일 상태
 */
export type TextSelectionStyle = {
  hasSelection: boolean;
  color?: string;
  strokeColor?: string;
  fontFamily?: string;
  fontWeight?: string;
  fontVariant?: string;
  fontSize?: string; // CSS에서는 문자열로 처리 (예: '24px')
  opacity?: string; // CSS에서는 문자열로 처리 (예: '0.5')
};

/**
 * 폰트 스타일 (weight + variant)
 */
export type FontStyleValue = {
  weight: string;
  variant: string;
};

/**
 * 텍스트 선택 상태 관리 컨텍스트
 */
export type TextSelectionContextType = {
  // 현재 선택 상태
  selectionStyle: TextSelectionStyle;
  setSelectionStyle: (style: TextSelectionStyle) => void;

  // 스타일 적용 함수
  applyColor: (color: string) => void;
  applyStrokeColor: (color: string) => void;
  applyFontFamily: (fontFamily: string) => void;
  applyFontStyle: (fontStyle: FontStyleValue) => void;
  applyFontSize: (fontSize: number) => void;
  applyOpacity: (opacity: number) => void;

  // Preview 함수 (선택 영역에 임시 적용)
  previewFontFamily: (fontFamily: string) => void;
  cancelFontFamilyPreview: () => void;
  previewFontStyle: (fontStyle: FontStyleValue) => void;
  cancelFontStylePreview: () => void;

  // 스타일 적용 함수 등록 (cleanup 함수 반환)
  registerColorApplier: (applier: (color: string) => void) => () => void;
  registerStrokeColorApplier: (applier: (color: string) => void) => () => void;
  registerFontFamilyApplier: (
    applier: (fontFamily: string) => void,
  ) => () => void;
  registerFontStyleApplier: (
    applier: (fontStyle: FontStyleValue) => void,
  ) => () => void;
  registerFontSizeApplier: (applier: (fontSize: number) => void) => () => void;
  registerOpacityApplier: (applier: (opacity: number) => void) => () => void;
  registerFontFamilyPreviewer: (
    previewer: (fontFamily: string) => void,
  ) => () => void;
  registerFontFamilyPreviewCanceler: (canceler: () => void) => () => void;
  registerFontStylePreviewer: (
    previewer: (fontStyle: FontStyleValue) => void,
  ) => () => void;
  registerFontStylePreviewCanceler: (canceler: () => void) => () => void;
};

const defaultSelectionStyle: TextSelectionStyle = {
  hasSelection: false,
};

export const TextSelectionContext = createContext<TextSelectionContextType>({
  selectionStyle: defaultSelectionStyle,
  setSelectionStyle: () => {},
  applyColor: () => {},
  applyStrokeColor: () => {},
  applyFontFamily: () => {},
  applyFontStyle: () => {},
  applyFontSize: () => {},
  applyOpacity: () => {},
  previewFontFamily: () => {},
  cancelFontFamilyPreview: () => {},
  previewFontStyle: () => {},
  cancelFontStylePreview: () => {},
  registerColorApplier: () => () => {},
  registerStrokeColorApplier: () => () => {},
  registerFontFamilyApplier: () => () => {},
  registerFontStyleApplier: () => () => {},
  registerFontSizeApplier: () => () => {},
  registerOpacityApplier: () => () => {},
  registerFontFamilyPreviewer: () => () => {},
  registerFontFamilyPreviewCanceler: () => () => {},
  registerFontStylePreviewer: () => () => {},
  registerFontStylePreviewCanceler: () => () => {},
});

/**
 * 텍스트 선택 컨텍스트 Provider
 */
export const TextSelectionProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [selectionStyle, setSelectionStyle] = useState<TextSelectionStyle>(
    defaultSelectionStyle,
  );

  // 색상 적용 함수 참조
  const colorApplierRef = React.useRef<((color: string) => void) | null>(null);
  const strokeColorApplierRef = React.useRef<((color: string) => void) | null>(
    null,
  );
  // 폰트 적용 함수 참조
  const fontFamilyApplierRef = React.useRef<
    ((fontFamily: string) => void) | null
  >(null);
  // 폰트 스타일 적용 함수 참조
  const fontStyleApplierRef = React.useRef<
    ((fontStyle: FontStyleValue) => void) | null
  >(null);
  // 폰트 사이즈 적용 함수 참조
  const fontSizeApplierRef = React.useRef<((fontSize: number) => void) | null>(
    null,
  );
  // 투명도 적용 함수 참조
  const opacityApplierRef = React.useRef<((opacity: number) => void) | null>(
    null,
  );
  // 폰트 preview 함수 참조
  const fontFamilyPreviewerRef = React.useRef<
    ((fontFamily: string) => void) | null
  >(null);
  const fontFamilyPreviewCancelerRef = React.useRef<(() => void) | null>(null);
  // 폰트 스타일 preview 함수 참조
  const fontStylePreviewerRef = React.useRef<
    ((fontStyle: FontStyleValue) => void) | null
  >(null);
  const fontStylePreviewCancelerRef = React.useRef<(() => void) | null>(null);

  const registerColorApplier = useCallback(
    (applier: (color: string) => void) => {
      colorApplierRef.current = applier;
      // cleanup 함수 반환
      return () => {
        if (colorApplierRef.current === applier) {
          colorApplierRef.current = null;
        }
      };
    },
    [],
  );

  const registerStrokeColorApplier = useCallback(
    (applier: (color: string) => void) => {
      strokeColorApplierRef.current = applier;
      return () => {
        if (strokeColorApplierRef.current === applier) {
          strokeColorApplierRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontFamilyApplier = useCallback(
    (applier: (fontFamily: string) => void) => {
      fontFamilyApplierRef.current = applier;
      return () => {
        if (fontFamilyApplierRef.current === applier) {
          fontFamilyApplierRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontStyleApplier = useCallback(
    (applier: (fontStyle: FontStyleValue) => void) => {
      fontStyleApplierRef.current = applier;
      return () => {
        if (fontStyleApplierRef.current === applier) {
          fontStyleApplierRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontSizeApplier = useCallback(
    (applier: (fontSize: number) => void) => {
      fontSizeApplierRef.current = applier;
      return () => {
        if (fontSizeApplierRef.current === applier) {
          fontSizeApplierRef.current = null;
        }
      };
    },
    [],
  );

  const registerOpacityApplier = useCallback(
    (applier: (opacity: number) => void) => {
      opacityApplierRef.current = applier;
      return () => {
        if (opacityApplierRef.current === applier) {
          opacityApplierRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontFamilyPreviewer = useCallback(
    (previewer: (fontFamily: string) => void) => {
      fontFamilyPreviewerRef.current = previewer;
      return () => {
        if (fontFamilyPreviewerRef.current === previewer) {
          fontFamilyPreviewerRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontFamilyPreviewCanceler = useCallback(
    (canceler: () => void) => {
      fontFamilyPreviewCancelerRef.current = canceler;
      return () => {
        if (fontFamilyPreviewCancelerRef.current === canceler) {
          fontFamilyPreviewCancelerRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontStylePreviewer = useCallback(
    (previewer: (fontStyle: FontStyleValue) => void) => {
      fontStylePreviewerRef.current = previewer;
      return () => {
        if (fontStylePreviewerRef.current === previewer) {
          fontStylePreviewerRef.current = null;
        }
      };
    },
    [],
  );

  const registerFontStylePreviewCanceler = useCallback(
    (canceler: () => void) => {
      fontStylePreviewCancelerRef.current = canceler;
      return () => {
        if (fontStylePreviewCancelerRef.current === canceler) {
          fontStylePreviewCancelerRef.current = null;
        }
      };
    },
    [],
  );

  const applyColor = useCallback((color: string) => {
    if (colorApplierRef.current) {
      colorApplierRef.current(color);
    }
  }, []);

  const applyStrokeColor = useCallback((color: string) => {
    if (strokeColorApplierRef.current) {
      strokeColorApplierRef.current(color);
    }
  }, []);

  const applyFontFamily = useCallback((fontFamily: string) => {
    if (fontFamilyApplierRef.current) {
      fontFamilyApplierRef.current(fontFamily);
    }
  }, []);

  const applyFontStyle = useCallback((fontStyle: FontStyleValue) => {
    if (fontStyleApplierRef.current) {
      fontStyleApplierRef.current(fontStyle);
    }
  }, []);

  const applyFontSize = useCallback((fontSize: number) => {
    if (fontSizeApplierRef.current) {
      fontSizeApplierRef.current(fontSize);
    }
  }, []);

  const applyOpacity = useCallback((opacity: number) => {
    if (opacityApplierRef.current) {
      opacityApplierRef.current(opacity);
    }
  }, []);

  const previewFontFamily = useCallback((fontFamily: string) => {
    if (fontFamilyPreviewerRef.current) {
      fontFamilyPreviewerRef.current(fontFamily);
    }
  }, []);

  const cancelFontFamilyPreview = useCallback(() => {
    if (fontFamilyPreviewCancelerRef.current) {
      fontFamilyPreviewCancelerRef.current();
    }
  }, []);

  const previewFontStyle = useCallback((fontStyle: FontStyleValue) => {
    if (fontStylePreviewerRef.current) {
      fontStylePreviewerRef.current(fontStyle);
    }
  }, []);

  const cancelFontStylePreview = useCallback(() => {
    if (fontStylePreviewCancelerRef.current) {
      fontStylePreviewCancelerRef.current();
    }
  }, []);

  const contextValue = React.useMemo(
    (): TextSelectionContextType => ({
      selectionStyle,
      setSelectionStyle,
      applyColor,
      applyStrokeColor,
      applyFontFamily,
      applyFontStyle,
      applyFontSize,
      applyOpacity,
      previewFontFamily,
      cancelFontFamilyPreview,
      previewFontStyle,
      cancelFontStylePreview,
      registerColorApplier,
      registerStrokeColorApplier,
      registerFontFamilyApplier,
      registerFontStyleApplier,
      registerFontSizeApplier,
      registerOpacityApplier,
      registerFontFamilyPreviewer,
      registerFontFamilyPreviewCanceler,
      registerFontStylePreviewer,
      registerFontStylePreviewCanceler,
    }),
    [
      selectionStyle,
      applyColor,
      applyStrokeColor,
      applyFontFamily,
      applyFontStyle,
      applyFontSize,
      applyOpacity,
      previewFontFamily,
      cancelFontFamilyPreview,
      previewFontStyle,
      cancelFontStylePreview,
      registerColorApplier,
      registerStrokeColorApplier,
      registerFontFamilyApplier,
      registerFontStyleApplier,
      registerFontSizeApplier,
      registerOpacityApplier,
      registerFontFamilyPreviewer,
      registerFontFamilyPreviewCanceler,
      registerFontStylePreviewer,
      registerFontStylePreviewCanceler,
    ],
  );

  return (
    <TextSelectionContext.Provider value={contextValue}>
      {children}
    </TextSelectionContext.Provider>
  );
};

/**
 * 텍스트 선택 컨텍스트 사용 훅
 */
export const useTextSelection = () => {
  return useContext(TextSelectionContext);
};
